import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Pencil,
  Trash2,
  Flame,
  Ban,
  CheckCircle2,
  Eye,
  EyeOff,
  Image as ImageIcon,
  Map as MapIcon,
  Mail,
  Share2,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageShell } from '@/components/primitives/PageShell';
import { PageHeader } from '@/components/primitives/PageHeader';
import { RoleGuard } from '@/components/primitives/RoleGuard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Marker, Polygon, Popup } from 'react-leaflet';
import L from 'leaflet';
import { MapCanvas } from '@/components/maps/MapCanvas';
import { WindParticleLayer } from '@/components/maps/wind/WindParticleLayer';
import { cardinalToDegrees, KMH_TO_KT } from '@/components/maps/wind/cardinal';
// Legacy .jsx subcomponents — kept structurally, only re-skinned later
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import MediaComponentRaw from '@/components/media/MediaComponent';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import WildfireDetailCardsRaw from '@/components/wildfires/WildfireDetailCards';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const MediaComponent = MediaComponentRaw as any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const WildfireDetailCards = WildfireDetailCardsRaw as any;

// Default + active icons — match the legacy MapComponent palette so the
// migration is a visual no-op (active fires get the red marker).
const defaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
const activeIcon = L.icon({
  iconUrl:
    'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
import { getWildfire, deleteWildfire, submitWildfire } from '@/api/wildfires';
import { useAuth } from '@/contexts/AuthContext';
import { WildfireShareSheet } from '@/components/wildfires/WildfireShareSheet';
import EmailReportDialog from './components/EmailReportDialog';

interface Wildfire {
  id: number;
  // The FPA the fire belongs to — the share sheet's mutual-aid picker needs
  // the FIRE's FPA, not the viewer's, to leave it out of its own options.
  tenant_id?: number | null;
  fire_reference?: string | null;
  type?: string | null;
  is_illegal?: boolean;
  member_id?: number | null;
  related_member_name?: string | null;
  user_id?: number | null;
  is_active?: boolean;
  is_public?: boolean;
  review_status?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  created_at?: string | null;
  suburb?: string | null;
  address?: string | null;
  internal_notes?: string | null;
  external_notes?: string | null;
  area_size_hectares?: number | null;
  location?: { coordinates: [number, number] } | null;
  area_polygon?: { coordinates: [number, number][][] } | null;
  user?: { username?: string | null } | null;
  weather_data?: WeatherEntry[] | null;
}

interface WeatherEntry {
  [key: string]: unknown;
}

const DEFAULT_CENTER: [number, number] = [-25.6429, 28.343353];

function formatDate(s: string | null | undefined): string {
  if (!s) return 'N/A';
  return new Date(s).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function calculateDuration(start?: string | null, end?: string | null): string {
  if (!start) return 'N/A';
  if (!end) return 'Ongoing';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  if (days > 0) return `${days} day${days !== 1 ? 's' : ''} ${hours} hr`;
  return `${hours} hour${hours !== 1 ? 's' : ''}`;
}

export default function WildfireDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isStaff, can, currentUser } = useAuth();

  const [wildfire, setWildfire] = useState<Wildfire | null>(null);
  const [weatherData, setWeatherData] = useState<WeatherEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!id) return;
      setLoading(true);
      setError(null);
      try {
        const data = (await getWildfire(id)) as unknown as Wildfire;
        if (cancelled) return;
        setWildfire(data);
        if (data.weather_data) setWeatherData(data.weather_data);
      } catch (err) {
        console.error('Failed to load wildfire:', err);
        if (!cancelled) setError('Failed to load wildfire details. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Map framing memos — declared before any early return so hook order stays
  // stable across the loading → loaded transition (React throws "rendered more
  // hooks than during the previous render" otherwise). Null-safe because
  // `wildfire` is null while loading.
  const mapCenter: [number, number] = useMemo(
    () =>
      wildfire?.location
        ? [wildfire.location.coordinates[1], wildfire.location.coordinates[0]]
        : DEFAULT_CENTER,
    [wildfire?.location],
  );
  const polygonCoords = useMemo(
    () =>
      wildfire?.area_polygon
        ? wildfire.area_polygon.coordinates[0]?.map(
            ([lng, lat]) => [lat, lng] as [number, number],
          )
        : null,
    [wildfire?.area_polygon],
  );

  // Frame the map on the fire instead of opening at a country-wide view. When
  // the fire has a burn polygon, fit to it (the marker rides inside). For a
  // point-only fire, skip the fit and let the static `center`/`zoom` below
  // sit on the fire — fitting a single point would force max zoom (too tight).
  // Memoised so it only fits once per fire; re-fitting on every gear/tab
  // toggle would fight the user's pan/zoom.
  const fitBoundsTo = useMemo(
    () =>
      polygonCoords && polygonCoords.length > 0
        ? {
            markers: [{ position: mapCenter }],
            polygons: [{ positions: polygonCoords }],
          }
        : (false as const),
    [mapCenter, polygonCoords],
  );

  if (loading) {
    return (
      <PageShell>
        <Skeleton className="mb-3 h-8 w-40" />
        <Skeleton className="mb-2 h-10 w-3/4" />
        <Skeleton className="mb-6 h-4 w-1/3" />
        <Skeleton className="h-48 w-full rounded-lg" />
      </PageShell>
    );
  }

  if (error || !wildfire) {
    return (
      <PageShell>
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error || 'Wildfire not found'}</AlertDescription>
        </Alert>
        <Button asChild variant="outline">
          <Link to="/wildfires">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to wildfires
          </Link>
        </Button>
      </PageShell>
    );
  }

  const handleDelete = async () => {
    if (!id) return;
    setDeleteOpen(false);
    try {
      await deleteWildfire(id);
      toast.success('Wildfire deleted');
      navigate('/wildfires');
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error('Failed to delete wildfire');
    }
  };

  const handleSubmitDraft = async () => {
    if (!id) return;
    try {
      await submitWildfire(id);
      toast.success('Report submitted');
      const data = (await getWildfire(id)) as unknown as Wildfire;
      setWildfire(data);
    } catch (err) {
      console.error('Submit failed:', err);
      toast.error('Failed to submit report');
    }
  };

  // Owners (e.g. FPA_USER) may edit their own report any time, but only delete
  // it while it's still a draft. Staff/admins with broad update/delete act on
  // any report. The button is hidden unless the user actually has the capability
  // (the backend enforces the same rule).
  const isOwner =
    currentUser != null && String(wildfire.user_id) === String(currentUser.id);
  const canEditThis = can('wildfire', 'update') && (isStaff() || isOwner);
  const canDeleteThis =
    can('wildfire', 'delete') &&
    (isStaff() || (isOwner && wildfire.review_status === 'draft'));

  return (
    <PageShell>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            <span>{wildfire.fire_reference || `Wildfire #${wildfire.id}`}</span>
            {wildfire.review_status === 'draft' && (
              <Badge variant="outline" className="gap-1 border-amber-400 text-amber-700">
                <Pencil className="h-3 w-3" /> Draft
              </Badge>
            )}
            {wildfire.is_active ? (
              <Badge variant="destructive" className="gap-1">
                <Flame className="h-3 w-3" /> Active
              </Badge>
            ) : (
              <Badge variant="secondary" className="gap-1">
                <CheckCircle2 className="h-3 w-3" /> Resolved
              </Badge>
            )}
            {wildfire.is_public ? (
              <Badge className="gap-1">
                <Eye className="h-3 w-3" /> Public
              </Badge>
            ) : (
              <Badge variant="outline" className="gap-1">
                <EyeOff className="h-3 w-3" /> Private
              </Badge>
            )}
            <Badge variant="outline" className="capitalize">
              {(wildfire.type ?? 'veld').replace('_', ' ')}
            </Badge>
            {wildfire.is_illegal && (
              <Badge variant="destructive" className="gap-1">
                <Ban className="h-3 w-3" /> Illegal
              </Badge>
            )}
          </span>
        }
        subtitle={
          <>
            Reported by {wildfire.user?.username || 'Unknown'} on{' '}
            {formatDate(wildfire.created_at)}
            {wildfire.related_member_name && (
              <> · Linked member: {wildfire.related_member_name}</>
            )}
          </>
        }
        breadcrumbs={[
          { label: 'Wildfires', to: '/wildfires' },
          { label: wildfire.fire_reference || `#${wildfire.id}` },
        ]}
        actions={
          <>
            {/* Ungated, unlike "Email report": that sends on the FPA's behalf,
                whereas this only hands whoever already opened the report the
                same facts in a message. The payload names no member. */}
            <Button variant="outline" size="sm" onClick={() => setShareOpen(true)}>
              <Share2 className="mr-1 h-4 w-4" />
              Share
            </Button>
            {wildfire.review_status === 'draft' && can('wildfire', 'approve') && (
              <Button size="sm" onClick={handleSubmitDraft}>
                <CheckCircle2 className="mr-1 h-4 w-4" />
                Submit report
              </Button>
            )}
            <RoleGuard staff>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEmailDialogOpen(true)}
              >
                <Mail className="mr-1 h-4 w-4" />
                Email report
              </Button>
            </RoleGuard>
            {canEditThis && (
              <Button asChild variant="outline" size="sm">
                <Link to={`/wildfires/${wildfire.id}/edit`}>
                  <Pencil className="mr-1 h-4 w-4" />
                  Edit
                </Link>
              </Button>
            )}
            {canDeleteThis && (
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => setDeleteOpen(true)}
              >
                <Trash2 className="mr-1 h-4 w-4" />
                Delete
              </Button>
            )}
          </>
        }
      />

      {/* Legacy detail cards (data summary), kept structurally for now */}
      <WildfireDetailCards
        wildfire={wildfire}
        weatherData={weatherData}
        formatDate={formatDate}
        calculateDuration={calculateDuration}
      />

      {(wildfire.internal_notes || wildfire.external_notes) && (
        <Card className="mt-4">
          <CardHeader>
            <CardTitle className="text-lg">Notes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {wildfire.internal_notes && (
              <div>
                <div className="text-sm font-medium text-muted-foreground">Internal notes</div>
                <p className="mt-1 whitespace-pre-wrap">{wildfire.internal_notes}</p>
              </div>
            )}
            {wildfire.external_notes && (
              <div>
                <div className="text-sm font-medium text-muted-foreground">External notes</div>
                <p className="mt-1 whitespace-pre-wrap">{wildfire.external_notes}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="map" className="mt-4">
        <TabsList>
          <TabsTrigger value="map">
            <MapIcon className="mr-2 h-4 w-4" />
            Map
          </TabsTrigger>
          <TabsTrigger value="images">
            <ImageIcon className="mr-2 h-4 w-4" />
            Images
          </TabsTrigger>
        </TabsList>

        <TabsContent value="map" className="space-y-3">
          {/* Wind toggle moved into the gear icon's settings panel
              (`showWindToggle` below). The standalone <Switch> is gone. */}
          <div className="h-[500px] w-full">
            <MapCanvas
              center={mapCenter}
              zoom={13}
              variant="inline"
              gearMode="full"
              showWindToggle
              tenantId={wildfire.tenant_id ?? null}
              mapSearch
              fitBoundsTo={fitBoundsTo}
              className="!h-full !min-h-0"
            >
              {({ settings }) => (
                <>
                  <Marker
                    position={mapCenter}
                    icon={wildfire.is_active ? activeIcon : defaultIcon}
                  >
                    <Popup>
                      <div>
                        <h3>{wildfire.fire_reference || `Wildfire #${wildfire.id}`}</h3>
                        <p>
                          <strong>Status:</strong>{' '}
                          <span style={{ color: wildfire.is_active ? '#e64a19' : '#2e7d32' }}>
                            {wildfire.is_active ? 'Active' : 'Resolved'}
                          </span>
                        </p>
                        <p>
                          <strong>Start:</strong> {formatDate(wildfire.start_time)}
                        </p>
                        <p>
                          <strong>End:</strong> {formatDate(wildfire.end_time)}
                        </p>
                        <p>
                          <strong>Area:</strong>{' '}
                          {wildfire.area_size_hectares?.toLocaleString() || 'N/A'} hectares
                        </p>
                      </div>
                    </Popup>
                  </Marker>

                  {polygonCoords && polygonCoords.length > 0 && (
                    <Polygon
                      positions={polygonCoords}
                      pathOptions={{
                        color: wildfire.is_active ? '#e64a19' : '#2e7d32',
                        fillColor: wildfire.is_active ? '#ff7d47' : '#60ad5e',
                        fillOpacity: 0.4,
                        weight: 2,
                      }}
                    />
                  )}

                  {settings.showWind &&
                    weatherData.length > 0 &&
                    /* The fire's freshest recorded reading drives a uniform
                       wind field — same animated style as the forecast map,
                       but flowing in the single direction this fire saw (so
                       it stays accurate for historical fires). */
                    (weatherData[0] as { wind_speed?: number; wind_direction?: string })
                      .wind_speed &&
                    (weatherData[0] as { wind_speed?: number; wind_direction?: string })
                      .wind_direction && (
                      <WindParticleLayer
                        show
                        basemap={settings.tileLayer}
                        uniform={{
                          speedKt:
                            ((weatherData[0] as { wind_speed?: number }).wind_speed ?? 0) *
                            KMH_TO_KT,
                          dirDeg: cardinalToDegrees(
                            (weatherData[0] as { wind_direction?: string }).wind_direction,
                          ),
                        }}
                      />
                    )}
                </>
              )}
            </MapCanvas>
          </div>
        </TabsContent>

        <TabsContent value="images">
          <div className="rounded-lg border border-border p-4">
            <MediaComponent wildfireId={id} formatDate={formatDate} />
          </div>
        </TabsContent>
      </Tabs>

      <WildfireShareSheet
        open={shareOpen}
        onOpenChange={setShareOpen}
        wildfire={wildfire}
        tenantId={wildfire.tenant_id ?? null}
        formatDateTime={formatDate}
      />

      <EmailReportDialog
        wildfireId={wildfire.id}
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
      />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this wildfire?</AlertDialogTitle>
            <AlertDialogDescription>
              Wildfire {wildfire.fire_reference || `#${wildfire.id}`} will be permanently
              removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageShell>
  );
}
